// Ring 0 — pure share-string formatter (D-EM-01..D-EM-05).
//
// SOLE producer of the Wordle-style emoji summary. Both [C] copy-text
// (clipboard.writeText) and [T] tweet-intent (URL `text` param) consume the
// SAME string from this module — no divergent formatting between paths.
//
// 280-char invariant (D-EM-05) is CI-mandatory: src/run/shareSummary.test.ts
// runs the full HandEval corpus and asserts length <= 280 for every case.
// A truncated tweet is audience-fatal (broken share = broken marketing lever).
//
// No PII present in the output — only seed + score + handName + 5 cards.
// All fields come from typed enums or pokersolver-derived strings; there is
// no user-supplied free text path that could inject newlines or `&`/`#` into
// the share URL (T-02-02, T-02-03).
//
// NO Phaser imports. NO Math.random. (CLAUDE.md anti-patterns.)

import type { CashierResult } from './RunState';
import type { Card } from './types';

const SUIT_GLYPH: Record<Card['suit'], string> = {
  h: '♥',
  d: '♦',
  c: '♣',
  s: '♠',
};

const PREMIUM_HANDS: ReadonlySet<string> = new Set([
  'Royal Flush',
  'Straight Flush',
  'Four of a Kind',
  'Full House',
  'Flush',
  'Straight',
]);

/**
 * UI-SPEC §Color: premium hands render the banner in gold #f5c542 on the
 * share card AND the RunOverScene hand-line. Both consumers MUST go through
 * this predicate to stay in sync.
 *
 * Match is case-sensitive on the descr label produced by evaluateHand
 * (e.g. pokersolver returns 'Royal Flush' for the descr — see Phase-1
 * D-03 corpus quirks documented in src/poker/handEval.test.ts).
 */
export function isPremiumHand(handName: string): boolean {
  return PREMIUM_HANDS.has(handName);
}

/**
 * D-EM-03: lower-32-bits of seed in base36, max 8 chars. Round-trips via the
 * BootScene parser (Plan 02-1 Task 1.2 extends the parser to accept base36).
 *
 * Examples:
 *   encodeSeedShort(0xDEADBEEF) === 'deadbeef'
 *   encodeSeedShort(1)          === '1'
 *   encodeSeedShort(42)         === '16'  (42 in base36)
 */
export function encodeSeedShort(seed: number): string {
  return (seed >>> 0).toString(36).slice(-8);
}

/**
 * D-EM-02 four-line format. CI-tested for 280-char fit across the HandEval
 * corpus (see shareSummary.test.ts). Output structure:
 *
 *   🃏 degen-kong {seedShort}
 *   {HAND_NAME_UPPER} · {score}
 *   {rank}{suit} {rank}{suit} {rank}{suit} {rank}{suit} {rank}{suit}
 *   {shareBaseUrl}?seed={seedShort}
 *
 * Suits use real Unicode chars (♠ ♥ ♦ ♣ — single code points U+2660..U+2667),
 * NOT regional-indicator emoji forms (which would be 2-codepoint sequences and
 * break tweet character counting per D-EM-02).
 */
export function formatShareSummary(
  cashier: CashierResult,
  seed: number,
  shareBaseUrl: string,
): string {
  const seedShort = encodeSeedShort(seed);
  const handLine = `${cashier.handName.toUpperCase()} · ${cashier.score.toLocaleString('en-US')}`;
  const cardsLine = cashier.locked5
    .map((c) => `${c.rank}${SUIT_GLYPH[c.suit]}`)
    .join(' ');
  const url = `${shareBaseUrl}?seed=${seedShort}`;
  return `🃏 degen-kong ${seedShort}\n${handLine}\n${cardsLine}\n${url}`;
}
